import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Edit, FileText, GitBranch, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, getOrganizationTreeSetupLabel, ORG_ORGANIZATION_PAGE_PATH, withServiceInstanceId } from '@/lib/service-instance'
import { getUserDropdownKeywords, getUserDropdownLabel, getUserRegistrationNumber } from '@/lib/user-display'
import { orgTreeApi, type OrgTreeNode } from '@/services/org-tree'
import { orgUsersApi } from '@/services/org-users'
import { useAuthStore } from '@/stores/auth-store'

const OWN_BRANCH_VALUE = '1'
const VENDOR_BRANCH_VALUE = '2'

export default function TreeNodeChildPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const currentPost = useAuthStore((state) => state.currentPost)
  const organizationTreeBackLabel = useMemo(
    () => getOrganizationTreeSetupLabel(
      currentPost?.service_variant_unique_id,
      currentPost?.service_variant_name ?? currentPost?.service_variant_type,
      currentPost?.level_variant_name,
    ),
    [currentPost?.level_variant_name, currentPost?.service_variant_name, currentPost?.service_variant_type, currentPost?.service_variant_unique_id],
  )
  const [search, setSearch] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [branchName, setBranchName] = useState('')
  const [selectedVariant, setSelectedVariant] = useState('')
  const [branchOwnership, setBranchOwnership] = useState(OWN_BRANCH_VALUE)
  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState('')
  const [selectedNode, setSelectedNode] = useState<OrgTreeNode | null>(null)

  const nodeQuery = useQuery({
    queryKey: ['org-tree-node', id],
    queryFn: () => orgTreeApi.getNode(id!),
    enabled: Boolean(id),
  })

  const childrenQuery = useQuery({
    queryKey: ['org-tree-children', id],
    queryFn: () => orgTreeApi.listChildren(id!),
    enabled: Boolean(id),
  })

  const variantsQuery = useQuery({
    queryKey: ['org-tree-instance-variants', serviceInstanceId],
    queryFn: () => orgTreeApi.listInstanceVariants(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId) && isCreateOpen,
  })

  const sourceInstanceQuery = useQuery({
    queryKey: ['org-tree-service-instance-source', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId) && isCreateOpen,
  })

  const ownerUsersQuery = useQuery({
    queryKey: ['branch-owner-users', serviceInstanceId],
    queryFn: () => orgUsersApi.list({ includeInactive: false, serviceInstanceId, pageSize: 1000 }),
    enabled: Boolean(serviceInstanceId) && isCreateOpen,
    staleTime: 60_000,
  })

  const activeOwnerUsers = useMemo(
    () => (ownerUsersQuery.data ?? []).filter((user) => user.is_active !== false),
    [ownerUsersQuery.data],
  )

  const vendorOwnerUsers = useMemo(
    () => activeOwnerUsers.filter((user) => {
      const groupCode = String(user.user_group_code ?? '').trim().toLowerCase()
      const groupTitle = String(user.user_group_title ?? '').trim().toLowerCase()
      return groupCode.includes('vendor') || groupTitle.includes('vendor')
    }),
    [activeOwnerUsers],
  )

  const ownerCandidates = useMemo(
    () => (branchOwnership === VENDOR_BRANCH_VALUE ? vendorOwnerUsers : activeOwnerUsers),
    [activeOwnerUsers, branchOwnership, vendorOwnerUsers],
  )

  const ownerUserOptions = useMemo(() => {
    const options = ownerCandidates.map((user) => {
      return {
        value: String(user.id),
        label: getUserDropdownLabel(user, user.id),
        keywords: getUserDropdownKeywords(user),
      }
    })

    const sourceOwnerId = String(sourceInstanceQuery.data?.owner_user_id ?? '').trim()
    if (branchOwnership === OWN_BRANCH_VALUE && sourceOwnerId && !options.some((option) => option.value === sourceOwnerId)) {
      const sourceOwnerProfile = sourceInstanceQuery.data?.owner_profile
      const sourceOwnerName = String(sourceOwnerProfile?.name ?? sourceInstanceQuery.data?.owner_name ?? '').trim()
      const sourceOwnerRegistrationNumber = getUserRegistrationNumber(sourceOwnerProfile ?? sourceInstanceQuery.data)
      const sourceOwnerPhone = String(sourceOwnerProfile?.phone ?? sourceInstanceQuery.data?.owner_phone ?? '').trim()
      const detail = [sourceOwnerRegistrationNumber, sourceOwnerPhone].filter(Boolean).join(' • ')
      options.unshift({
        value: sourceOwnerId,
        label: detail ? `${sourceOwnerName || `User ${sourceOwnerId}`} • ${detail}` : sourceOwnerName || `User ${sourceOwnerId}`,
        keywords: [sourceOwnerName, sourceOwnerRegistrationNumber, sourceOwnerPhone].filter(Boolean).join(' '),
      })
    }

    return options
  }, [branchOwnership, ownerCandidates, sourceInstanceQuery.data])

  useEffect(() => {
    if (!isCreateOpen || branchOwnership !== OWN_BRANCH_VALUE || selectedOwnerUserId) {
      return
    }

    const ownerUserId = String(sourceInstanceQuery.data?.owner_user_id ?? '').trim()
    if (ownerUserId && ownerUserOptions.some((option) => option.value === ownerUserId)) {
      setSelectedOwnerUserId(ownerUserId)
    }
  }, [branchOwnership, isCreateOpen, ownerUserOptions, selectedOwnerUserId, sourceInstanceQuery.data?.owner_user_id])

  const filteredChildren = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) {
      return childrenQuery.data ?? []
    }

    return (childrenQuery.data ?? []).filter((child) =>
      [child.node_name, child.service_variant_name, child.level_variant_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    )
  }, [childrenQuery.data, search])

  const createMutation = useMutation({
    mutationFn: () => orgTreeApi.createChildBranch({
      name: branchName.trim(),
      service_variant: selectedVariant,
      org_instance_id: serviceInstanceId!,
      branch_ownership: branchOwnership,
      owner_user_id: selectedOwnerUserId,
    }),
    onSuccess: async () => {
      toast.success('Branch created successfully')
      setIsCreateOpen(false)
      setBranchName('')
      setSelectedVariant('')
      setBranchOwnership(OWN_BRANCH_VALUE)
      setSelectedOwnerUserId('')
      await queryClient.invalidateQueries({ queryKey: ['org-tree-children', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => orgTreeApi.updateNode(selectedNode!.id, { node_name: branchName.trim() }),
    onSuccess: async () => {
      toast.success('Branch updated successfully')
      setIsEditOpen(false)
      setSelectedNode(null)
      setBranchName('')
      await queryClient.invalidateQueries({ queryKey: ['org-tree-children', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => orgTreeApi.deleteNode(selectedNode!.id),
    onSuccess: async () => {
      toast.success('Branch deleted successfully')
      setIsDeleteOpen(false)
      setSelectedNode(null)
      await queryClient.invalidateQueries({ queryKey: ['org-tree-children', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleCreate = () => {
    if (!branchName.trim()) {
      toast.error('Branch name is required')
      return
    }

    if (!selectedVariant) {
      toast.error('App type is required')
      return
    }

    if (!branchOwnership) {
      toast.error('Branch ownership is required')
      return
    }

    if (!selectedOwnerUserId) {
      toast.error('Branch owner is required')
      return
    }

    createMutation.mutate()
  }

  const handleOpenEdit = (node: OrgTreeNode) => {
    setSelectedNode(node)
    setBranchName(node.node_name || '')
    setIsEditOpen(true)
  }

  const handleOpenDelete = (node: OrgTreeNode) => {
    setSelectedNode(node)
    setIsDeleteOpen(true)
  }

  if (nodeQuery.isLoading || childrenQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-[28px]" />
        <Skeleton className="h-96 w-full rounded-[28px]" />
      </div>
    )
  }

  if (nodeQuery.error || childrenQuery.error || !nodeQuery.data) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Failed to load branches</p>
        <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(nodeQuery.error || childrenQuery.error)}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId))}>
          {`Back to ${organizationTreeBackLabel}`}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Branches</h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                Organization branches under {nodeQuery.data.node_name || `node ${nodeQuery.data.id}`} are listed below. Add branches, search the list, or open a row to manage details and policies.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" asChild>
                <Link to={withServiceInstanceId(`/org/node/${id}`, serviceInstanceId)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Node
                </Link>
              </Button>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Branch
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[28px] border-border/80">
        <CardHeader className="gap-4 border-b border-border/70 pb-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <CardTitle className="text-lg">Child branch directory</CardTitle>
              <CardDescription>Rename, remove, or open the policy page for branch nodes attached to this organization node.</CardDescription>
            </div>
            <Input
              placeholder="Search branches or variants"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="overflow-hidden rounded-2xl border border-border/70 bg-background">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="pl-5">Branch</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead className="pr-5 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredChildren.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-28 text-center text-sm text-muted-foreground">
                      No child branches found for this node.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredChildren.map((child) => (
                    <TableRow key={String(child.id)}>
                      <TableCell className="pl-5">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                            <GitBranch className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{child.node_name || `Node ${child.id}`}</p>
                            <p className="text-xs text-muted-foreground">ID: {String(child.id)}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{child.service_variant_name || child.level_variant_name || child.service_variant || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{String(child.parent ?? id)}</TableCell>
                      <TableCell className="pr-5">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={withServiceInstanceId(`/org/node/branch-instrance/${child.id}`, serviceInstanceId)}>
                              <GitBranch className="mr-1 h-4 w-4" />
                              Instances
                            </Link>
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={withServiceInstanceId(`/org/node-details/${child.id}`, serviceInstanceId)}>
                              <FileText className="mr-1 h-4 w-4" />
                              Details
                            </Link>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(child)}>
                            <Edit className="mr-1 h-4 w-4" />
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleOpenDelete(child)}>
                            <Trash2 className="mr-1 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Branch</DialogTitle>
            <DialogDescription>Create a child branch node with a service type and ownership for this organization context.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="branch-name">Branch name</Label>
              <Input id="branch-name" value={branchName} onChange={(event) => setBranchName(event.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="node-branch-service-type">App Type</Label>
              <Select
                value={selectedVariant}
                onValueChange={setSelectedVariant}
                disabled={createMutation.isPending || variantsQuery.isLoading || !(variantsQuery.data ?? []).length}
              >
                <SelectTrigger id="node-branch-service-type" aria-label="App type">
                  <SelectValue placeholder={variantsQuery.isLoading ? 'Loading app types...' : 'Select an app type'} />
                </SelectTrigger>
                <SelectContent>
                  {(variantsQuery.data ?? []).map((variant) => (
                    <SelectItem key={String(variant.id)} value={variant.service_variant || String(variant.id)}>
                      {variant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="node-branch-ownership">Branch ownership</Label>
              <Select value={branchOwnership} onValueChange={setBranchOwnership} disabled={createMutation.isPending}>
                <SelectTrigger id="node-branch-ownership" aria-label="Branch ownership">
                  <SelectValue placeholder="Select branch ownership" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OWN_BRANCH_VALUE}>Own Branch</SelectItem>
                  <SelectItem value={VENDOR_BRANCH_VALUE}>Vendor Branch</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Vendor branch means this branch is handled by an external company.</p>
            </div>

            <div className="space-y-2">
              <Label>Branch owner</Label>
              <SearchableSelect
                ariaLabel="Branch owner"
                value={selectedOwnerUserId}
                onValueChange={setSelectedOwnerUserId}
                options={ownerUserOptions}
                placeholder={ownerUsersQuery.isLoading ? 'Loading users...' : 'Choose branch owner'}
                searchPlaceholder="Search users"
                emptyText={ownerUsersQuery.isLoading ? 'Loading users...' : 'No users found.'}
                disabled={ownerUsersQuery.isLoading || ownerUserOptions.length === 0}
              />
              <p className="text-xs text-muted-foreground">
                {branchOwnership === VENDOR_BRANCH_VALUE
                  ? ownerCandidates.length === 0
                    ? 'No vendor users found in the current Organization. Add a vendor user first.'
                    : 'Showing users in the vendors user group.'
                  : 'The current organization or tenant owner is selected by default.'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending || !serviceInstanceId}>
              Create Branch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Branch</DialogTitle>
            <DialogDescription>Rename the selected child branch node.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="edit-branch-name">Branch name</Label>
            <Input id="edit-branch-name" value={branchName} onChange={(event) => setBranchName(event.target.value)} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Branch</DialogTitle>
            <DialogDescription>Remove the selected child branch node from the Tree structure.</DialogDescription>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            {selectedNode?.node_name || 'This branch'} will be deleted. Continue only if you are sure.
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
              Delete Branch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}